import { isNumber } from 'lodash';
import React, { useContext } from 'react';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { type IFontGlow, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import ColorInputGroup from '@/components/color-input-group';
import { SimpleEffectPropsContainer } from '@/components/simple-effect-props-container';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '@/panels/text-layer/context';
import { useLayerProps } from '@/panels/text-layer/hooks';
import { Tracker } from '@/panels/text-layer/utils';

const FALLBACK_COLOR = '#000000ff';

export const GlowInspector: React.FC = () => {
    const { layerId, recordId, isIntl, onOpenSubPanel } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const glow = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textGlow = layerData.textGlow || [];

        return textGlow.length > 0 ? textGlow[0] : null;
    }) as IFontGlow | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const hidden = glow ? Boolean(glow.hidden) : true;
    const color = glow ? glow.color : FALLBACK_COLOR;
    const width = glow ? Math.round(glow.width) : 0;

    const add = () => {
        if (glow) return;
        editor.getInstance(TextLayerExtension).addGlow(layerId);

        Tracker.addGlow({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'shortcut',
        });
    };

    const remove = () => {
        if (!glow) return;
        editor.getInstance(TextLayerExtension).removeEffect(layerId, 'textGlow');
    };

    const setVisibility = (flag: boolean) => {
        if (!glow) return;
        editor.getInstance(TextLayerExtension).setEffectVisibility(layerId, 'textGlow', flag);

        Tracker.toggleGlow({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'shortcut',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'shortcut',
                kind: 'textGlow',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!glow) return;

        const payload = { idx: 0, data: { color: value } };

        editor.getInstance(TextLayerExtension).adjustGlowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustGlowProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'shortcut',
            });
        }
    };

    const adjustWidth = (value: number | null, options = { writeHistory: true }) => {
        if (!glow || !isNumber(value)) return;

        const width = Math.round(value);
        const payload = { idx: 0, data: { width } };
        editor.getInstance(TextLayerExtension).adjustGlowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustGlowProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'shortcut',
            });
        }
    };

    return (
        <SimpleEffectPropsContainer
            title={$t((v) => v.textStyleAdd.glow)}
            showAdd={glow == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
            onJump2SubPanel={() => onOpenSubPanel('textEffect', 'glow')}
        >
            {glow ? (
                <ColorInputGroup
                    disabled={hidden}
                    width={width}
                    color={color}
                    showInput={!isIntl}
                    onChange={(val) => adjustWidth(val, { writeHistory: false })}
                    onChangeAfter={(val) => adjustWidth(val, { writeHistory: true })}
                    onAdjustColor={(color) => adjustColor(color, { writeHistory: false })}
                    onAdjustColorAfter={(color, isBrandColor) => {
                        adjustColor(color, { writeHistory: true });
                        if (isBrandColor) {
                            window._stat_track?.('hb_layers_func_use', {
                                func_id: '1_107',
                            });
                        }
                    }}
                    onColorPickerOpenChange={handleColorPickerOpenChange}
                    onToggleBizTab={(bizTab) => {
                        if (bizTab === 'team') {
                            window._stat_track?.('hb_layers_func_click', {
                                func_id: '1_107',
                            });
                        }
                    }}
                />
            ) : null}
        </SimpleEffectPropsContainer>
    );
};
